// Duty times in the admin website. A duty's starttime / endtime (and its alerts' times) are moments in
// milliseconds. A duty is entered and shown in its LOCATION's time zone (locations.timezone), with that
// zone's summer time on the duty's own date, so the admin, guard, supervisor and client screens show the
// same clock time (the server does the same: App\DutyTime in Laravel).
// Uses the browser's own time zone data (Intl), not a fixed offset: tz-offset and "the offset now" were
// wrong for duties on the other side of a clock change.
// Note "GMT" has no summer time: a UK location must be "Europe/London".
// A missing or broken time (undefined, null, '', NaN, "abc") never throws: Intl's formatToParts throws a
// RangeError on an Invalid Date, which blanked the whole duty page. Such a time is shown as '--:--' / ''.

// True when the browser knows this zone name.
export function isKnownZone(zone) {
	if (!zone || typeof zone !== 'string') {
		return false;
	}
	try {
		new Intl.DateTimeFormat('en-GB', { timeZone: zone });
		return true;
	} catch (e) {
		return false;
	}
}

// A moment in milliseconds (a number, a numeric string or a Date), or null when it is missing or not a
// usable time. null, '', blanks and true/false are missing (Number() would make them 0 or 1: 1970).
function toMs(ms) {
	if (ms === undefined || ms === null || typeof ms === 'boolean') {
		return null;
	}
	if (typeof ms === 'string' && ms.trim() === '') {
		return null;
	}
	if (typeof ms === 'object' && !(ms instanceof Date)) {
		return null;
	}
	const n = Number(ms);
	if (!Number.isFinite(n) || isNaN(new Date(n).getTime())) {
		return null;
	}
	return n;
}

// The clock in a zone at a moment: { year, month (1-12), day, hour, minute }. An unknown or empty zone
// uses this browser's own zone. null for a missing or broken time.
export function clockIn(ms, zone) {
	const moment = toMs(ms);
	if (moment === null) {
		return null;
	}
	const options = {
		year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
		hourCycle: 'h23',
	};
	if (isKnownZone(zone)) {
		options.timeZone = zone;
	}
	const parts = {};
	new Intl.DateTimeFormat('en-GB', options).formatToParts(new Date(moment)).forEach((part) => {
		if (part.type !== 'literal') {
			parts[part.type] = parseInt(part.value, 10);
		}
	});
	return {
		year: parts.year, month: parts.month, day: parts.day,
		hour: parts.hour === 24 ? 0 : parts.hour, minute: parts.minute, second: parts.second,
	};
}

// The zone's offset from UTC in minutes at that moment (60 for London in summer, 0 in winter). NaN for a
// missing or broken time.
export function offsetMinutes(ms, zone) {
	const c = clockIn(ms, zone);
	if (c === null) {
		return NaN;
	}
	const asUtc = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second);
	return Math.round((asUtc - Math.floor(toMs(ms) / 1000) * 1000) / 60000);
}

// The moment of a clock time in a zone (month 1-12). The offset is found for that date, so summer time
// is right whatever today is. A time in the hour skipped in spring comes out an hour later. NaN when the
// date or time is not numbers.
export function momentOf(year, month, day, hour, minute, zone) {
	const asUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
	if (isNaN(asUtc)) {
		return NaN;
	}
	let ms = asUtc - offsetMinutes(asUtc, zone) * 60000;
	ms = asUtc - offsetMinutes(ms, zone) * 60000;
	return ms;
}

// The date picker shows a clock time with the browser's Date: the clock time it shows is taken as the
// location's clock time. NaN when nothing (or an Invalid Date) is picked.
export function pickedToMoment(date, zone) {
	if (!(date instanceof Date) || isNaN(date.getTime())) {
		return NaN;
	}
	return momentOf(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), zone);
}

// A moment as a Date for the date picker, showing the location's clock time. null (an empty picker) for
// a missing or broken time.
export function momentToPicked(ms, zone) {
	const c = clockIn(ms, zone);
	if (c === null) {
		return null;
	}
	return new Date(c.year, c.month - 1, c.day, c.hour, c.minute, 0, 0);
}

function two(n) {
	return n < 10 ? '0' + n : String(n);
}

// "2026-7-1 09:00:00", as the duty lists showed it, in the zone (the location's). '' for a missing or
// broken time.
export function formatDutyDateTime(ms, zone) {
	const c = clockIn(ms, zone);
	if (c === null) {
		return '';
	}
	return c.year + '-' + c.month + '-' + c.day + ' ' + two(c.hour) + ':' + two(c.minute) + ':00';
}

// "09:00" in the zone. '--:--' for a missing or broken time.
export function formatDutyClock(ms, zone) {
	const c = clockIn(ms, zone);
	if (c === null) {
		return '--:--';
	}
	return two(c.hour) + ':' + two(c.minute);
}
